import { CaretDown, Check } from '@phosphor-icons/react';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Multiselect genérico con búsqueda: botón + panel de checkboxes. Convención de
 * `seleccion`: Set vacío = "Todas" (sin filtro); con elementos = solo esos.
 *
 * `onTodas`/`onNinguna` son opcionales (no se muestran si no se pasan). Cierra al
 * hacer click afuera. Self-contained (sin dependencia de Popover).
 */
export interface OpcionMulti {
  value: string;
  label: string;
}

export function MultiSelectPopup({
  titulo,
  opciones,
  seleccion,
  onToggle,
  onTodas,
  onNinguna,
  disabled = false,
  ancho = 'w-72',
}: {
  titulo: string;
  opciones: OpcionMulti[];
  seleccion: Set<string>;
  onToggle: (value: string) => void;
  onTodas?: () => void;
  onNinguna?: () => void;
  disabled?: boolean;
  ancho?: string;
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [abierto]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? opciones.filter((o) => o.label.toLowerCase().includes(q)) : opciones;
  }, [opciones, busca]);

  const n = seleccion.size;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAbierto((v) => !v)}
        className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${
          n > 0
            ? 'border-violet-400 bg-violet-50 text-violet-800'
            : 'border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50'
        } disabled:cursor-not-allowed disabled:border-zinc-200 disabled:bg-zinc-100 disabled:text-zinc-400`}
        title={titulo}
      >
        {titulo}
        <span className="text-[10px] opacity-70">({n === 0 ? 'Todas' : n})</span>
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && !disabled && (
        <div
          className={`absolute left-0 z-50 mt-1 ${ancho} overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg`}
        >
          <div className="border-b border-zinc-100 p-2">
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar…"
              className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
            />
            {(onTodas || onNinguna) && (
              <div className="mt-1.5 flex gap-2 text-[11px]">
                {onTodas && (
                  <button type="button" onClick={onTodas} className="text-blue-600 hover:underline">
                    Todas
                  </button>
                )}
                {onNinguna && (
                  <button
                    type="button"
                    onClick={onNinguna}
                    className="text-blue-600 hover:underline"
                  >
                    Ninguna
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtradas.map((o) => {
              const checked = seleccion.has(o.value);
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => onToggle(o.value)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-zinc-50"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked
                        ? 'border-violet-600 bg-violet-600 text-white'
                        : 'border-zinc-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3" weight="bold" />}
                  </span>
                  <span className="flex-1 truncate" title={o.label}>
                    {o.label}
                  </span>
                </button>
              );
            })}
            {filtradas.length === 0 && (
              <p className="px-3 py-1.5 text-[11px] text-muted-foreground">Sin resultados</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
